import { useEffect, useState } from "react";
import { Input } from "../../primitives/Input";
import { listInventoryMovements } from "../../../api/catalog";
import { BACKEND_INVENTORY_UNAVAILABLE_MSG } from "../../../api/auth";

export type MovementRow = {
  id: string;
  date: string;
  product_name: string;
  product_id: string;
  type: string;
  quantity: number;
  before: number;
  after: number;
  source: string;
  user: string;
};

function toMovementRow(item: {
  id: string;
  product_id: string;
  product_name: string;
  movement_type: string;
  quantity_delta: number;
  quantity_before: number | null;
  quantity_after: number | null;
  reference: string | null;
  source_type: string | null;
  created_by_user_id: string | null;
  created_at: string | null;
}): MovementRow {
  const source = item.reference || item.source_type || "";
  const user = item.created_by_user_id ? String(item.created_by_user_id).slice(0, 8) + "…" : "—";
  return {
    id: item.id,
    date: item.created_at || "",
    product_name: item.product_name,
    product_id: item.product_id,
    type: item.movement_type,
    quantity: item.quantity_delta,
    before: item.quantity_before ?? 0,
    after: item.quantity_after ?? 0,
    source,
    user,
  };
}

export function InventoryRegistre() {
  const [rows, setRows] = useState<MovementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [moduleError, setModuleError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<keyof MovementRow>("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listInventoryMovements({ page_size: 100 })
      .then((res) => {
        if (!cancelled) {
          setRows((res.items || []).map(toMovementRow));
          setModuleError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setRows([]);
          setModuleError(err instanceof Error ? err.message : null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const filtered = rows.filter(
    (r) =>
      !search ||
      r.product_name.toLowerCase().includes(search.toLowerCase()) ||
      r.type.toLowerCase().includes(search.toLowerCase()) ||
      r.source.toLowerCase().includes(search.toLowerCase())
  );
  const sorted = [...filtered].sort((a, b) => {
    const va = a[sortKey];
    const vb = b[sortKey];
    const cmp = typeof va === "string" ? String(va).localeCompare(String(vb)) : Number(va) - Number(vb);
    return sortDir === "asc" ? cmp : -cmp;
  });

  const th = (key: keyof MovementRow, label: string) => (
    <th
      className="text-left p-3 font-medium text-text cursor-pointer hover:bg-surface-muted/50"
      onClick={() => {
        setSortKey(key);
        setSortDir((d) => (sortKey === key ? (d === "asc" ? "desc" : "asc") : "desc"));
      }}
    >
      {label} {sortKey === key && (sortDir === "asc" ? "↑" : "↓")}
    </th>
  );

  return (
    <div className="p-6 space-y-4">
      {moduleError === BACKEND_INVENTORY_UNAVAILABLE_MSG && (
        <div className="rounded-md border border-warning bg-warning/10 p-4 text-sm text-warning">
          {moduleError}
        </div>
      )}
      <h1 className="text-xl font-semibold text-text">Registre d'activité</h1>
      <p className="text-sm text-muted">Journal de tous les mouvements de stock. Aucune édition possible.</p>
      <div className="min-w-[240px]">
        <Input label="Recherche" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Produit, type, source..." />
      </div>
      {loading ? (
        <p className="text-sm text-muted">Chargement...</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-muted">
                {th("date", "Date")}
                {th("product_name", "Produit")}
                {th("type", "Type")}
                {th("quantity", "Quantité")}
                {th("before", "Avant")}
                {th("after", "Après")}
                {th("source", "Source")}
                {th("user", "Utilisateur")}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-muted">
                    Aucun mouvement. Le registre sera alimenté par les ajustements, réceptions et réservations.
                  </td>
                </tr>
              ) : (
                sorted.map((r) => (
                  <tr key={r.id} className="border-b border-border hover:bg-surface-muted/50">
                    <td className="p-3 text-muted">{new Date(r.date).toLocaleString()}</td>
                    <td className="p-3 font-medium text-text">{r.product_name}</td>
                    <td className="p-3">{r.type}</td>
                    <td className="p-3 text-right">{r.quantity}</td>
                    <td className="p-3 text-right">{r.before}</td>
                    <td className="p-3 text-right">{r.after}</td>
                    <td className="p-3">{r.source}</td>
                    <td className="p-3 text-muted">{r.user}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
